import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { motion } from "framer-motion";
import { Clock, FileCheck2, Upload, CreditCard, Award, Star, ShieldCheck, ArrowRight, Check, Instagram, Mail, Phone } from "lucide-react";

const STEPS = [
  { icon: FileCheck2, title: "Fragebogen ausfüllen", desc: "Beantworten Sie ein paar Fragen zu Ihrem Gründungsvorhaben." },
  { icon: Upload, title: "Unterlagen hochladen oder per E-Mail schicken", desc: "Businessplan, Lebenslauf und den Antrag auf Stellungnahme hochladen – oder alles per E-Mail an frank.saier@outlook.de senden." },
  { icon: CreditCard, title: "Online bezahlen", desc: "Per PayPal, Kreditkarte, Apple Pay oder Überweisung." },
  { icon: Clock, title: "In 48h fertig", desc: "Ihre Stellungnahme erhalten Sie per E-Mail oder per Post." },
];

const FAQ = [
  { q: "Was ist eine fachkundige Stellungnahme?", a: "Für den Gründungszuschuss verlangt die Agentur für Arbeit eine fachkundige Stellungnahme, die die Tragfähigkeit Ihrer Existenzgründung bestätigt. Diese darf u. a. von IHK, Steuerberatern und fachkundigen Beratern ausgestellt werden." },
  { q: "Wie schnell erhalte ich meine Stellungnahme?", a: "Mit dem Express-Paket erhalten Sie Ihre unterschriebene Stellungnahme garantiert innerhalb von 48 Stunden nach vollständigem Eingang Ihrer Unterlagen." },
  { q: "Welche Unterlagen benötige ich?", a: "In der Regel Businessplan, Finanz- bzw. Liquiditätsplan, Lebenslauf und ggf. Ihren ALG-I-Bescheid. Fehlende Unterlagen können wir gemeinsam erarbeiten." },
  { q: "Was kostet der Service?", a: "Je nach Paket zwischen 129 € und 249 €. Die Businessplan-Beratung ist ab 220 € erhältlich." },
  { q: "Ist die Stellungnahme anerkannt?", a: "Ja. Unsere Stellungnahmen werden von fachkundigen Stellen ausgestellt und von der Agentur für Arbeit anerkannt." },
];

export default function Landing() {
  const nav = useNavigate();
  const [packages, setPackages] = useState([]);
  useEffect(() => { api.get("/packages").then((r) => setPackages(r.data)).catch(() => {}); }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative bg-[#0A192F] text-white overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-60" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 rounded-full px-4 py-1.5 mb-6 font-medium">
              <Clock size={14} className="mr-1.5" /> Fertig in nur 48 Stunden
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-none">
              Fachkundige Stellungnahme für Ihren <span className="text-emerald-400">Gründungszuschuss</span>
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-xl">
              Die von der Agentur für Arbeit geforderte Stellungnahme – professionell erstellt, unterschrieben und auf Wunsch innerhalb von 48 Stunden geliefert.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row sm:flex-wrap gap-3">
              <Button size="lg" data-testid="hero-cta" onClick={() => nav("/antrag")}
                className="bg-emerald-500 hover:bg-emerald-600 text-white text-base h-12 px-8">
                Jetzt Stellungnahme anfordern <ArrowRight size={18} className="ml-2" />
              </Button>
              <Button size="lg" variant="outline" data-testid="hero-pricing"
                onClick={() => document.getElementById("preise")?.scrollIntoView({ behavior: "smooth" })}
                className="border-white/25 bg-white/5 text-white hover:bg-white/10 h-12 px-8">
                Preise ansehen
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-400">
              <span className="flex items-center gap-1.5"><Check size={16} className="text-emerald-400" /> Von fachkundiger Stelle</span>
              <span className="flex items-center gap-1.5"><Check size={16} className="text-emerald-400" /> DSGVO-konform</span>
              <span className="flex items-center gap-1.5"><Check size={16} className="text-emerald-400" /> Sichere Zahlung</span>
            </div>
          </div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
            <img src="https://images.unsplash.com/photo-1780733064275-d1ade9b83a3d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Beratungsgespräch am Schreibtisch" className="w-full h-[420px] object-cover" />
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold">So funktioniert's</h2>
        <p className="mt-3 text-muted-foreground max-w-2xl">In vier einfachen Schritten zu Ihrer fachkundigen Stellungnahme.</p>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-4 gap-6">
          {STEPS.map((s, i) => (
            <Card key={i} data-testid={`step-${i}`} className="p-6 border-border shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <span className="grid place-items-center h-11 w-11 rounded-md bg-[#0A192F] text-emerald-400"><s.icon size={22} /></span>
                <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Schritt {i + 1}</span>
              </div>
              <h3 className="font-semibold text-lg">{s.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{s.desc}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Expert */}
      <section className="bg-white py-20 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid md:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <div className="absolute -inset-3 bg-emerald-500/10 rounded-2xl -z-0" />
            <img src="https://customer-assets-4nw71qhi.emergentagent.net/job_expert-opinion-2/artifacts/uxxbh6y0_image.png"
              alt="Dipl.-Volksw. Frank Saier – Fachkundiger Gutachter & Gründungsberater" className="relative rounded-2xl w-full max-w-sm object-cover border border-border shadow-md" />
          </div>
          <div>
            <span className="text-xs font-semibold uppercase tracking-widest text-emerald-600">Ihr Gutachter</span>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold">Dipl.-Volksw. Frank Saier</h2>
            <p className="mt-1 text-muted-foreground font-medium">Fachkundiger Gutachter & Gründungsberater</p>
            <p className="mt-5 text-foreground/90 max-w-xl">
              Als zertifizierter Gutachter prüfe ich Ihr Gründungsvorhaben persönlich, bespreche mit Ihnen Ihren Businessplan oder schreibe den Businessplan für Sie und erstelle dann die Stellungnahme für die Agentur für Arbeit – schnell, konkret, unkompliziert und garantiert innerhalb von 48 Stunden.
              <br /><br />
              Seit über 30 Jahren begleite ich Gründerinnen und Gründer erfolgreich in die Selbständigkeit.
              <br /><br />
              Damit auch aus Ihrer Idee ein Erfolg wird!
            </p>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="preise" className="bg-muted/40 border-y border-border py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold">Preise & Pakete</h2>
          <p className="mt-3 text-muted-foreground">Transparent, ohne versteckte Kosten. Alle Preise verstehen sich zzgl. 19% USt, inkl. Rechnung.</p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {packages.map((p) => (
              <Card key={p.lookup_key} data-testid={`pkg-${p.lookup_key}`}
                className={`p-6 flex flex-col relative ${p.popular ? "border-2 border-[#0A192F] shadow-md" : "border-border"}`}>
                {p.popular && <Badge className="absolute -top-3 left-6 bg-emerald-500 text-white rounded-full"><Clock size={12} className="mr-1" /> 48h Express</Badge>}
                <h3 className="font-semibold text-lg mt-1">{p.name}</h3>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-4xl font-extrabold font-display">{p.price} €</span>
                </div>
                <span className="text-xs text-muted-foreground mt-1">zzgl. 19% USt = {(p.price * 1.19).toFixed(2).replace(".", ",")} €</span>
                <p className="mt-2 text-sm text-muted-foreground flex-1">{p.desc}</p>
                <Button data-testid={`pkg-cta-${p.lookup_key}`} onClick={() => nav(`/antrag?pkg=${p.lookup_key}`)}
                  className={`mt-6 w-full ${p.popular ? "bg-emerald-500 hover:bg-emerald-600 text-white" : "bg-[#0A192F] hover:bg-[#172A45] text-white"}`}>
                  Auswählen
                </Button>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold">Das sagen unsere Gründer</h2>
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {[
            { n: "Sarah M.", t: "Innerhalb eines Tages hatte ich meine Stellungnahme – der Zuschuss wurde problemlos bewilligt!" },
            { n: "Tobias K.", t: "Super professionell und schnell. Die Rückfragen wurden direkt im Portal geklärt." },
            { n: "Aylin D.", t: "Der Businessplan-Check war Gold wert. Klare Empfehlung für jeden Gründer." },
          ].map((r, i) => (
            <Card key={i} className="p-6 border-border shadow-sm">
              <div className="flex gap-0.5 text-emerald-500 mb-3">{[...Array(5)].map((_, k) => <Star key={k} size={16} fill="currentColor" />)}</div>
              <p className="text-sm text-foreground">"{r.t}"</p>
              <div className="mt-4 flex items-center gap-2 text-sm font-semibold"><Award size={16} className="text-[#0A192F]" /> {r.n}</div>
            </Card>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-muted/40 border-y border-border py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold">Häufige Fragen</h2>
          <Accordion type="single" collapsible className="mt-8">
            {FAQ.map((f, i) => (
              <AccordionItem key={i} value={`i${i}`} data-testid={`faq-${i}`}>
                <AccordionTrigger className="text-left font-semibold">{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A192F] text-white py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <ShieldCheck size={40} className="mx-auto text-emerald-400" />
          <h2 className="mt-4 text-3xl sm:text-4xl font-bold">Starten Sie noch heute</h2>
          <p className="mt-3 text-slate-300">Ihre fachkundige Stellungnahme wartet – in nur 48 Stunden.</p>
          <Button size="lg" data-testid="footer-cta" onClick={() => nav("/antrag")}
            className="mt-8 bg-emerald-500 hover:bg-emerald-600 text-white h-12 px-8">
            Jetzt Stellungnahme anfordern <ArrowRight size={18} className="ml-2" />
          </Button>
          <p className="mt-8 text-slate-400 text-sm">Sie haben Fragen? Schreiben Sie uns direkt:</p>
          <div className="mt-2 flex flex-col sm:flex-row items-center justify-center gap-x-6 gap-y-2 text-slate-200">
            <a href="mailto:frank.saier@outlook.de" data-testid="cta-email" className="flex items-center gap-2 font-semibold hover:text-emerald-400 transition-colors"><Mail size={18} /> frank.saier@outlook.de</a>
            <a href="tel:+49766162209" data-testid="cta-phone" className="flex items-center gap-2 font-semibold hover:text-emerald-400 transition-colors"><Phone size={18} /> 07661 62209</a>
          </div>
        </div>
      </section>

      <footer className="bg-[#0A192F] border-t border-white/10 text-slate-400 text-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-8 pb-8 flex flex-col sm:flex-row items-center gap-5 border-b border-white/10">
          <a href="https://instagram.com/gruendungszuschuss48h" target="_blank" rel="noopener noreferrer" data-testid="footer-ig-qr" className="shrink-0">
            <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https%3A%2F%2Finstagram.com%2Fgruendungszuschuss48h" alt="Instagram QR-Code" className="h-24 w-24 rounded-md bg-white p-1.5" />
          </a>
          <div className="text-center sm:text-left">
            <div className="flex items-center justify-center sm:justify-start gap-2 text-white font-semibold"><Instagram size={18} /> @gruendungszuschuss48h</div>
            <p className="mt-1">Folgen Sie uns auf Instagram – QR-Code scannen oder antippen. Tipps rund um Gründungszuschuss & Existenzgründung.</p>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row justify-between gap-4">
          <span>© {new Date().getFullYear()} www.gruendungszuschuss48h.com · Tel. 07661 62209 · frank.saier@outlook.de</span>
          <div className="flex items-center gap-6">
            <a href="https://instagram.com/gruendungszuschuss48h" target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" className="hover:text-white flex items-center gap-1.5"><Instagram size={16} /> Instagram</a>
            <Link to="/impressum" className="hover:text-white">Impressum</Link><Link to="/datenschutz" className="hover:text-white">Datenschutz</Link><Link to="/agb" className="hover:text-white">AGB</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
